import { combineLatest, Subject, Subscription, takeUntil } from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormArray, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-common-form-container',
  templateUrl: './common-form-container.component.html',
  // tl-card (tailwind.scss) is the shared card surface. No 1-5 spacing steps here: Bootstrap 4
  // declares .p-5 / .mb-4 ... with !important and they would win.
  host: { class: 'tl-card mb-6 flex flex-col', '[class.overflow-hidden]': 'flushContent' },
})
export class CommonFormContainerComponent implements OnDestroy {
  readonly destroy$ = new Subject<void>();
  //Inputs
  @Input() header: string = '';
  private _formGroup: UntypedFormGroup;
  public get formGroup(): UntypedFormGroup {
    return this._formGroup;
  }
  @Input()
  public set formGroup(value: UntypedFormGroup) {
    this._formGroup = value;
    this.setupFormGroup(value);
  }

  formGroupStatusChangeSubscription: Subscription;

  @Input() showActionControls: boolean = true;
  /** Hides only Save (Reset stays) for a page that owns one consolidated save, e.g. tank Set Up. */
  @Input() showSaveButton: boolean = true;
  /** Content sits flush to the card edges (no body padding), e.g. a map. */
  @Input() flushContent: boolean = false;

  @Input() useParentForm: boolean = true;

  @Input() saveButtonText: string | null = 'General.SaveAllChanges';

  tooltipText: string | null;
  @Input()
  public set tooltip_text(value: string) {
    // instant() throws on an empty key, and consumers bind this to values that can be unset.
    this.tooltipText = value ? this.translate.instant(value) : null;
  }

  //Button Events
  @Output()
  resetClick: EventEmitter<UntypedFormGroup> = new EventEmitter();
  @Output()
  saveClick: EventEmitter<UntypedFormGroup> = new EventEmitter();

  private setupFormGroup(value: UntypedFormGroup) {
    this._formGroup = value;
    this.setupFormGroupStatusChangeSubscription();
  }

  setupFormGroupStatusChangeSubscription() {
    //unsubscribe from previous subscription
    if (this.formGroupStatusChangeSubscription) {
      this.formGroupStatusChangeSubscription.unsubscribe();
      this.formGroupStatusChangeSubscription = null;
      }

    //detect changes to the form group status and value
    this.formGroupStatusChangeSubscription = combineLatest([
      this.formGroup.statusChanges,
      this.formGroup.valueChanges,
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([status]) => {
      this._cdr.detectChanges();
      //console.log('Form Status Changed', status);
    });
  }

  get saveDisabled(): boolean {
    if (this.useParentForm === true) {
      return !this.formGroup?.dirty || this.formGroup?.invalid;
    } else {
      const rtnVal = !this.formGroup?.dirty;
      // const rtnVal = !this.formGroup?.dirty || this.formGroup?.invalid;
      return rtnVal;
    }
  }

  get resetDisabled(): boolean {
    return !this.formGroup?.dirty;
  }

  constructor(
    private translate: TranslateService,
    private _cdr: ChangeDetectorRef
  ) { }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onResetClicked() {
    //Loop through all the controls in the form group
    // reset the control to the value originally set in the control state
    this.resetFormGroup(this.formGroup);
    this.resetClick.emit(this.formGroup);
  }

  resetFormGroup(formGroup: any) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      //if the control is a FormArray
      if (control instanceof FormArray) {
        (control as FormArray).controls.forEach((fg) => {
          this.resetFormGroup(fg);
        });
      }
      //if the control is a FormGroup
      else if (control instanceof UntypedFormGroup) {
        this.resetFormGroup(control);
      }
      //the control is a FormControl
      else {
        try {
          if (
            control['originalValue'] !== null ||
            control['originalValue'] !== undefined
          ) {
            control.reset(control['originalValue']);
          } else {
            //reset to component/template defaults if not originalValue is set
            control.reset();
          }
        } catch (error) {
          console.error('Error resetting control', key, control, error);
        }
      }
      //mark the control as pristine and untouched
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  onSaveClicked() {
    //guard against save if form is pristine or invalid
    //if (this.formGroup.pristine) return; //SBTMTAPWP-1637 removed this check to allow save of pristine form (Save All for FormGroups siblings)
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) return;

    //emit save event if form is valid
    this.saveClick.emit(this?.formGroup);
  }
}
